import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, money, dateFmt } from "@/lib/api";
import { Bridge, MapPin, Tag, CheckCircle, ArrowRight } from "@phosphor-icons/react";

export default function InvitePage() {
  const { id } = useParams();
  const { data: req, isLoading, isError } = useQuery({
    queryKey: ["invite", id], queryFn: () => api.publicRateRequest(id), retry: false,
  });

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center p-6">
      <div className="w-full max-w-xl bg-white border border-[#111827] p-8" data-testid="invite-page">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center"><Bridge size={20} weight="bold" color="#fff" /></div>
          <span className="font-display font-bold text-xl">RateBridge</span>
        </Link>

        {isLoading && <p className="text-[#4B5563]">Loading invite…</p>}
        {isError && (
          <div data-testid="invite-error">
            <h1 className="font-display font-black text-2xl text-[#111827]">Invite not found</h1>
            <p className="text-sm text-[#4B5563] mt-2">This link may be invalid or expired.</p>
            <Link to="/" className="inline-block mt-4 text-[#002FA7] font-semibold hover:underline">Go to RateBridge →</Link>
          </div>
        )}

        {req && (
          <div>
            <span className="overline text-[#002FA7]">You have a rate request</span>
            <h1 className="font-display font-black text-3xl text-[#111827] mt-2">{req.hotel_name}</h1>
            <div className="flex items-center gap-1 text-sm text-[#4B5563] mt-1">
              <MapPin size={14} /> {req.address || `${req.city}${req.state ? ", " + req.state : ""}` || "—"}
            </div>

            <div className="mt-6 border border-[#E5E7EB] p-5 bg-[#F9FAFB]" data-testid="invite-details">
              <p className="text-sm text-[#4B5563]">
                <span className="font-semibold text-[#111827]">{req.company_name}</span> wants to book with you and has named a price:
              </p>
              <div className="flex items-baseline gap-2 mt-3">
                <Tag size={22} weight="fill" color="#002FA7" />
                <span className="font-display font-black text-4xl text-[#002FA7]">{money(req.nightly_price)}</span>
                <span className="text-[#4B5563]">/ night</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div><div className="overline text-[#4B5563]">Dates</div><div className="text-[#111827]">{dateFmt(req.check_in)} – {dateFmt(req.check_out)}</div></div>
                <div><div className="overline text-[#4B5563]">Rooms</div><div className="text-[#111827]">{req.num_rooms} rooms · {req.room_nights} room-nights</div></div>
              </div>
              {req.notes && <div className="mt-4 text-sm"><div className="overline text-[#4B5563]">Notes</div><div className="text-[#111827]">{req.notes}</div></div>}
            </div>

            {req.status === "claimed" ? (
              <div className="mt-6 flex items-center gap-2 text-[#047857] font-semibold" data-testid="invite-claimed">
                <CheckCircle size={18} weight="fill" /> This request has already been picked up on RateBridge.
              </div>
            ) : (
              <>
                <p className="text-sm text-[#4B5563] mt-6">
                  List your hotel on RateBridge to respond directly — no OTAs, no middleman. Set up takes a minute.
                </p>
                <Link to="/register" data-testid="invite-register-cta"
                  className="mt-4 inline-flex items-center gap-2 bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98]">
                  Claim this request — join RateBridge <ArrowRight size={18} weight="bold" />
                </Link>
                <p className="text-sm text-[#4B5563] mt-4">
                  Already listed? <Link to="/login" className="text-[#002FA7] font-semibold hover:underline">Sign in</Link> and open <b>Rate Requests</b>.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
